import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Beaker, TrendingUp, Building2, Check } from "lucide-react";

type PersonaKey = "test" | "scale" | "brand";

const PERSONAS: { key: PersonaKey; tabKey: string; iconNode: ReactNode }[] = [
    { key: "test", tabKey: "landing.persona.test.tab", iconNode: <Beaker size={16} /> },
    { key: "scale", tabKey: "landing.persona.scale.tab", iconNode: <TrendingUp size={16} /> },
    { key: "brand", tabKey: "landing.persona.brand.tab", iconNode: <Building2 size={16} /> },
];

const POINTS: Record<PersonaKey, string[]> = {
    test: ["landing.persona.test.p1", "landing.persona.test.p2", "landing.persona.test.p3"],
    scale: ["landing.persona.scale.p1", "landing.persona.scale.p2", "landing.persona.scale.p3"],
    brand: ["landing.persona.brand.p1", "landing.persona.brand.p2", "landing.persona.brand.p3"],
};

const VISUAL_BARS: Record<PersonaKey, number[]> = {
    test: [22, 38, 18, 55, 30, 70, 42, 28],
    scale: [40, 55, 48, 62, 70, 75, 82, 88],
    brand: [60, 65, 62, 70, 68, 74, 72, 78],
};

const VISUAL_KPIS: Record<PersonaKey, { labelKey: string; valueKey: string }[]> = {
    test: [
        { labelKey: "landing.persona.test.kpi1Label", valueKey: "landing.persona.test.kpi1Value" },
        { labelKey: "landing.persona.test.kpi2Label", valueKey: "landing.persona.test.kpi2Value" },
        { labelKey: "landing.persona.test.kpi3Label", valueKey: "landing.persona.test.kpi3Value" },
    ],
    scale: [
        { labelKey: "landing.persona.scale.kpi1Label", valueKey: "landing.persona.scale.kpi1Value" },
        { labelKey: "landing.persona.scale.kpi2Label", valueKey: "landing.persona.scale.kpi2Value" },
        { labelKey: "landing.persona.scale.kpi3Label", valueKey: "landing.persona.scale.kpi3Value" },
    ],
    brand: [
        { labelKey: "landing.persona.brand.kpi1Label", valueKey: "landing.persona.brand.kpi1Value" },
        { labelKey: "landing.persona.brand.kpi2Label", valueKey: "landing.persona.brand.kpi2Value" },
        { labelKey: "landing.persona.brand.kpi3Label", valueKey: "landing.persona.brand.kpi3Value" },
    ],
};

export const PersonaSwitcher = () => {
    const { t } = useTranslation("common");
    const [active, setActive] = useState<PersonaKey>("scale");

    return (
        <section id="personas" className="py-20 sm:py-28">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3">
                        {t("landing.persona.eyebrow")}
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold font-display text-text-primary mb-4">
                        {t("landing.persona.title")}
                    </h2>
                    <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400">
                        {t("landing.persona.subtitle")}
                    </p>
                </div>

                <div className="flex justify-center mb-10 sm:mb-12">
                    <div className="inline-flex bg-white dark:bg-neutral-900 p-1 rounded-lg border border-neutral-200 dark:border-neutral-800 shadow-sm">
                        {PERSONAS.map((p) => {
                            const isActive = p.key === active;
                            return (
                                <button
                                    key={p.key}
                                    onClick={() => setActive(p.key)}
                                    className={`flex items-center gap-2 px-3 sm:px-5 py-2 rounded-md text-sm font-medium transition-colors ${isActive
                                        ? "bg-text-primary dark:bg-accent-primary text-white shadow-sm"
                                        : "text-neutral-500 dark:text-neutral-400 hover:text-text-primary dark:hover:text-neutral-200"
                                        }`}
                                >
                                    {p.iconNode}
                                    <span>{t(p.tabKey)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="relative max-w-6xl mx-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35 }}
                            className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center"
                        >
                            <div className="lg:col-span-2 order-2 lg:order-1">
                                <h3 className="text-2xl sm:text-3xl font-bold font-display text-text-primary mb-3">
                                    {t(`landing.persona.${active}.title`)}
                                </h3>
                                <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-6 leading-relaxed">
                                    {t(`landing.persona.${active}.desc`)}
                                </p>
                                <ul className="space-y-3">
                                    {POINTS[active].map((pk) => (
                                        <li key={pk} className="flex items-start gap-3">
                                            <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-primary/10 text-accent-primary shrink-0">
                                                <Check size={12} strokeWidth={3} />
                                            </span>
                                            <span className="text-sm sm:text-base text-text-primary leading-relaxed">
                                                {t(pk)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Visual */}
                            <div className="lg:col-span-3 order-1 lg:order-2">
                                <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-xl p-5 sm:p-7 relative overflow-hidden">
                                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-accent-primary/5 rounded-full blur-3xl" aria-hidden />
                                    <div className="relative">
                                        <div className="flex items-center justify-between mb-5">
                                            <div>
                                                <p className="text-[10px] font-semibold tracking-wider uppercase text-neutral-400 mb-1">
                                                    {t(`landing.persona.${active}.chartLabel`)}
                                                </p>
                                                <p className="text-2xl sm:text-3xl font-bold font-display text-text-primary">
                                                    {t(`landing.persona.${active}.chartValue`)}
                                                </p>
                                            </div>
                                            <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-500">
                                                {t(`landing.persona.${active}.chartTag`)}
                                            </span>
                                        </div>

                                        <div className="flex items-end gap-1.5 sm:gap-2 h-28 sm:h-36 mb-6">
                                            {VISUAL_BARS[active].map((h, i) => (
                                                <motion.div
                                                    key={i}
                                                    initial={{ height: 0 }}
                                                    animate={{ height: `${h}%` }}
                                                    transition={{ duration: 0.6, delay: i * 0.04, ease: "easeOut" }}
                                                    className="flex-1 bg-gradient-to-t from-accent-primary/30 to-accent-primary rounded-t-md"
                                                />
                                            ))}
                                        </div>

                                        <div className="grid grid-cols-3 gap-3">
                                            {VISUAL_KPIS[active].map((kpi, i) => (
                                                <motion.div
                                                    key={kpi.labelKey}
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.3 + i * 0.06 }}
                                                    className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-800/50 p-3"
                                                >
                                                    <p className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1">
                                                        {t(kpi.labelKey)}
                                                    </p>
                                                    <p className="text-base sm:text-lg font-semibold text-text-primary tabular-nums">
                                                        {t(kpi.valueKey)}
                                                    </p>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};
